import { useParams } from "react-router";
import {
  useHtzOrderPutMutation,
  useHtzOrderShowQuery,
} from "@/services/backend/htz/orders/api/order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { rtkErrIsValidationError } from "@/shared/lib/errors/validation-error";
import { useMemo, useState } from "react";
import { Order } from "@/services/backend/htz/orders/types/order";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import {
  PositionsTable,
  PositionsTableSkeleton,
} from "@/services/backend/htz/position/components/positions-table";
import {
  PositionsTableContext,
  PositionsTableContextInterface,
} from "@/services/backend/htz/position/components/positions-table-context";
import { PositionCatalog } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { usePositionsTableDependencies } from "@/services/backend/htz/position/hooks/use-positions-table-dependencies";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { ErrValidationFailed } from "@bitory-gmbh/nidavellir";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";

export function PositionsRoute() {
  const { orderId } = useParams();

  const {
    data: order,
    isLoading: orderIsLoading,
    error: orderError,
  } = useHtzOrderShowQuery({
    id: orderId!,
  });

  const {
    catalog,
    airHandlingUnits,
    isLoading: depsIsLoading,
    error: depsError,
  } = usePositionsTableDependencies(order?.companyId, order?.customerId);

  if (orderError || depsError) {
    return <RTKQueryErrorAlert error={orderError || depsError} />;
  }

  if (orderIsLoading || depsIsLoading) {
    return <PositionsTableSkeleton />;
  }

  return (
    <OrderPositionsUpdater
      order={order!}
      positionCatalog={catalog!}
      airHandlingUnits={airHandlingUnits!}
    />
  );
}

interface OrderPositionsUpdaterProps {
  order: Order;
  positionCatalog: PositionCatalog;
  airHandlingUnits: AirHandlingUnit[];
}

function OrderPositionsUpdater({
  order,
  positionCatalog,
  airHandlingUnits,
}: OrderPositionsUpdaterProps) {
  const { notReadyError, setNotReadyError } = useErrDocumentNotReady();

  const [request, setRequest] = useState({ ...order });
  const mutation = useHtzOrderPutMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    ...order,
  });

  useRefEffect(order.offerId, () => {
    setRequest(order);
  });

  const validationError =
    notReadyError ?? (rtkErrIsValidationError(error) ? error.data : null);

  const value: PositionsTableContextInterface = useMemo(
    () => ({
      positions: request.positions,
      onPositionsChange: (positions) => setRequest({ ...request, positions }),
      immutable: false,
      validationError,
      setValidationError: setNotReadyError as (
        err: ErrValidationFailed | null,
      ) => void,
      positionCatalog,
      airHandlingUnits,
    }),
    [
      request,
      validationError,
      setNotReadyError,
      positionCatalog,
      airHandlingUnits,
    ],
  );

  return (
    <PositionsTableContext.Provider value={value}>
      <PositionsTable />
    </PositionsTableContext.Provider>
  );
}
